import { getProgressBar } from '../../progress-bar/executeSteps'
import { FieldType } from '@wix/wix-data-schema-types'
import type { PageAddResult } from '../add-cms-pages/addCmsPages'
import { addItemPage } from '../add-dynamic-item-page/addDynamicItemPage'
import { NotificationType } from '@wix/editor-platform-sdk-types'
import {
  PAGE_TYPES,
  runTransactionWithRetry,
  UNDO_REDO_LABELS,
} from '@wix/wix-data-client-common'

import type {
  AppApi,
  Editor3DynamicPagesBiParams,
} from '@wix/wix-data-client-common'
import type {
  EditorSDK,
  FedopsLogger,
  I18n,
  Logger,
} from '@wix/wix-data-client-common-standalone'
import {
  dynamicPageCustomRouterCreatePageClicked,
  itemPageNavigationToRepeaterSrc79Evid488,
} from '@wix/bi-logger-cms-data/v2'
import type { PresetDefinition } from '../../presets/types'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type { ComponentRef } from '@wix/editor-platform-sdk-types'
import { generateUniquePageLinkId } from '../../slug/generatePageLinkId'
import { addMainSlugFieldIfNotExists } from '../../slug/addSlugField'

type Params = {
  editorSdkProxy: EditorSDK
  appApi: AppApi
  preset: PresetDefinition
  collectionsApi: CollectionsApi
  existingCollectionId: string
  i18n: I18n
  fedopsLogger: FedopsLogger
  logger: Logger
  componentRef: ComponentRef
  controllerRef: ComponentRef
  biParams: Editor3DynamicPagesBiParams
}

const getComponentRole = async ({
  editorSdkProxy,
  appApi,
  componentRef,
}: {
  editorSdkProxy: EditorSDK
  appApi: AppApi
  componentRef: ComponentRef
}): Promise<string> => {
  const [{ sdkType }] = await editorSdkProxy.components.get({
    componentRefs: [componentRef],
    properties: ['sdkType'],
  })

  const bindingDefinition = await appApi.getBindingDefinition({
    componentType: sdkType,
  })

  return bindingDefinition.sections[0].role
}

const linkItemPagesToComponent = async ({
  editorSdkProxy,
  appApi,
  componentRef,
  controllerRef,
  pageLinkFieldKey,
}: {
  editorSdkProxy: EditorSDK
  appApi: AppApi
  componentRef: ComponentRef
  controllerRef: ComponentRef
  pageLinkFieldKey: string
}) => {
  const role = await getComponentRole({
    editorSdkProxy,
    appApi,
    componentRef,
  })

  await appApi.connectBindingSection({
    componentRef,
    controllerRef,
    newBindings: [
      {
        type: 'Field',
        prop: 'link',
        fieldPath: pageLinkFieldKey,
        fieldType: FieldType.pageLink,
        linkTarget: '_self',
      },
    ],
    role,
    requiresPrimaryConnection: true,
  })
}

const interactionName = 'addDynamicItemPageAndLinkToComponent'

export const addDynamicItemPageAndLinkToComponent = async ({
  editorSdkProxy,
  appApi,
  preset,
  collectionsApi,
  existingCollectionId,
  i18n,
  fedopsLogger,
  logger,
  componentRef,
  controllerRef,
  biParams,
}: Params) => {
  fedopsLogger.interactionStarted(interactionName)

  const progressBar = getProgressBar({
    editorSdkProxy,
    logger,
    totalSteps: 2,
    title: i18n.t('Editor3_Add_Pages_Progress_Bar_Title_Creating_Item_Pages'),
  })
  await progressBar.open()

  const componentPageRef = await editorSdkProxy.pages.getCurrent()

  let result: PageAddResult

  try {
    await progressBar.update({
      stepTitle: i18n.t(
        'Editor3_Add_Pages_Progress_Bar_Step_Title_Adding_Items_Page',
      ),
    })

    await addMainSlugFieldIfNotExists({
      collectionsApi,
      collectionId: existingCollectionId,
    })

    const pageLinkId = await generateUniquePageLinkId({ editorSdkProxy })

    result = await runTransactionWithRetry(editorSdkProxy, async () => {
      const result = await addItemPage({
        editorSdkProxy,
        appApi,
        preset,
        collectionsApi,
        existingCollectionId,
        pageLinkId,
      })

      await progressBar.update({
        stepTitle: i18n.t(
          'Editor3_Add_Pages_Progress_Bar_Step_Title_Linking_Pages',
        ),
      })

      await linkItemPagesToComponent({
        editorSdkProxy,
        appApi,
        componentRef,
        controllerRef,
        pageLinkFieldKey: pageLinkId,
      })

      return result
    })

    await progressBar.update()
  } finally {
    await progressBar.close()
  }

  const {
    newRouterPrefix,
    pattern,
    schema: newCollectionSchema,
    pageRef,
  } = result
  logger.bi(
    dynamicPageCustomRouterCreatePageClicked({
      page_type: PAGE_TYPES.ITEM,
      collection_name: newCollectionSchema.displayName,
      collection_id: existingCollectionId,
      new_url: `${newRouterPrefix}${pattern}`,
      is_auto_bound: true,
      new_collection: false,
      pageId: pageRef.id,
      action_type: 'add_dynamic_page',
      origin: biParams?.origin,
      template_id: preset.id,
    }),
  )

  await appApi.handleChangedRouters({ pageRefs: [pageRef] })

  await editorSdkProxy.history.add({
    label: UNDO_REDO_LABELS.UPDATE_ROUTERS,
    params: { pageRefs: [pageRef] },
  })

  fedopsLogger.interactionEnded(interactionName)

  await editorSdkProxy.pages.navigateTo({ pageRef })

  const onLinkClick = () => {
    logger.bi(
      itemPageNavigationToRepeaterSrc79Evid488({
        page_id: pageRef.id,
        action_source: 'create_item_page',
        action: 'go_to_repeater',
        repeater_id: componentRef.id,
      }),
    )
    editorSdkProxy.pages.navigateTo({ pageRef: componentPageRef })
  }

  appApi.showToastNotification(
    {
      message: i18n.t(
        'Editor3_Add_Pages_Toast_Success_Message_Item_Pages_Created_And_Linked',
      ),
      type: NotificationType.Success,
      linkText: i18n.t(
        'Editor3_Add_Pages_Toast_Success_Link_Text_Back_To_Repeater_Page',
      ),
    },
    onLinkClick,
  )

  return { pageRef }
}
